if (!customElements.get('lw-cart-complements')) {
  class CartComplements extends HTMLElement {
    static adding = false;

    connectedCallback() {
      this.onChangeBound = this.onChange.bind(this);
      this.onSubmitBound = this.onSubmit.bind(this);
      this.addEventListener('change', this.onChangeBound);
      this.addEventListener('submit', this.onSubmitBound);
      if (typeof subscribe === 'function') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (!event.complementsRendered) return this.refresh();
        });
      }
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChangeBound);
      this.removeEventListener('submit', this.onSubmitBound);
      this.unsubscribe?.();
    }

    onChange(event) {
      event.stopPropagation();
      const option = event.target.selectedOptions?.[0];
      const price = event.target.closest('.lw-cart-complement')?.querySelector('[data-lw-price]');
      if (price && option) price.textContent = option.dataset.price;
    }

    sections() {
      const sections = [];
      for (const id of ['main-cart-items', 'main-cart-footer']) {
        const target = document.getElementById(id);
        if (target) sections.push({ id: target.dataset.id, target: `#${id}`, source: `#${id}` });
      }
      if (document.getElementById('cart-icon-bubble')) {
        sections.push({ id: 'cart-icon-bubble', target: '#cart-icon-bubble', source: '.shopify-section' });
      }
      if (document.getElementById('CartDrawer')) {
        sections.push({ id: 'cart-drawer', target: '#CartDrawer', source: '#CartDrawer' });
      }
      return sections;
    }

    renderSections(htmlSections, sections) {
      const replacements = sections.map((section) => {
        const html = htmlSections?.[section.id];
        if (!html) throw new Error('Missing cart section');
        const content = new DOMParser().parseFromString(html, 'text/html').querySelector(section.source);
        const target = document.querySelector(section.target);
        if (!content || !target) throw new Error('Missing cart content');
        return { target, content };
      });
      const notes = [...document.querySelectorAll('cart-note textarea')].map((note) => [note.id, note.value]);
      replacements.forEach(({ target, content }) => { target.innerHTML = content.innerHTML; });
      const drawer = document.querySelector('cart-drawer');
      const overlay = drawer?.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', () => drawer.close());
      if (drawer?.classList.contains('active') && typeof trapFocus === 'function') {
        trapFocus(drawer.querySelector('#CartDrawer'), drawer.querySelector('.drawer__close'));
      }
      notes.forEach(([id, value]) => {
        const note = document.getElementById(id);
        if (note) note.value = value;
      });
    }

    async refresh() {
      if (!this.isConnected || CartComplements.adding) return;
      const sectionId = this.dataset.context === 'drawer' ? 'cart-drawer' : document.getElementById('main-cart-footer')?.dataset.id;
      if (!sectionId) return;
      try {
        const url = new URL(window.routes.cart_url, window.location.origin);
        url.searchParams.set('sections', sectionId);
        const response = await fetch(url);
        const sections = await response.json();
        if (!this.isConnected || CartComplements.adding || !sections[sectionId]) return;
        const html = new DOMParser().parseFromString(sections[sectionId], 'text/html');
        const updated = html.querySelector(`lw-cart-complements[data-context="${this.dataset.context}"]`);
        if (updated) this.replaceWith(updated);
        else this.remove();
      } catch (_) {}
    }

    async onSubmit(event) {
      const form = event.target.closest('.lw-cart-complement__form');
      if (!form) return;
      event.preventDefault();
      event.stopPropagation();
      if (CartComplements.adding) return;
      CartComplements.adding = true;
      const sections = this.sections();
      const buttons = [...document.querySelectorAll('.lw-cart-complement__add')];
      const button = form.querySelector('button[type="submit"]');
      const originalText = button.textContent;
      const addedText = this.dataset.added;
      const error = form.querySelector('[role="alert"]');
      const variantId = new FormData(form).get('id');
      let added = false;
      let deferred;
      error.hidden = true;
      buttons.forEach((item) => { item.disabled = true; });
      button.textContent = this.dataset.adding;
      this.setAttribute('aria-busy', 'true');
      try {
        const eventType = window.StandardEvents?.CartLinesUpdateEvent;
        if (eventType) {
          deferred = eventType.createPromise();
          this.dispatchEvent(new eventType({ action: 'add', context: 'cart', lines: [{ merchandiseId: variantId, quantity: 1 }], promise: deferred.promise }));
        }
        const data = new FormData(form);
        data.set('sections', sections.map((section) => section.id).join(','));
        data.set('sections_url', window.location.pathname);
        const response = await fetch(window.routes.cart_add_url, { method: 'POST', headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: data });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || this.dataset.error);
        added = true;
        document.dispatchEvent(new CustomEvent('lw:p019-added', { detail: {
          metadata: result.properties?._lw_p019, variant_id: String(result.variant_id || variantId),
        } }));
        if (deferred) {
          fetch(`${window.routes.cart_url}.js`).then((value) => value.json()).then((cart) => {
            deferred.resolve({ cart: eventType.createCartFromAjaxResponse(cart) });
          }).catch((failure) => deferred.reject(failure));
        }
        this.renderSections(result.sections, sections);
        if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', complementsRendered: true, productVariantId: variantId, cartData: result });
        const context = this.dataset.context;
        const region = document.querySelector(context === 'drawer' ? '#CartDrawer' : '#main-cart-footer');
        const heading = region?.querySelector('lw-cart-complements h2') || region?.querySelector('.totals');
        if (heading) { heading.setAttribute('tabindex', '-1'); heading.focus({ preventScroll: true }); }
        const live = document.getElementById(context === 'drawer' ? 'CartDrawer-LiveRegionText' : 'cart-live-region-text');
        if (live) { live.textContent = addedText; live.setAttribute('aria-hidden', 'false'); }
      } catch (failure) {
        if (added) { window.location.assign(window.routes.cart_url); return; }
        deferred?.reject(failure);
        error.textContent = failure.message || this.dataset.error;
        error.hidden = false;
      } finally {
        CartComplements.adding = false;
        this.removeAttribute('aria-busy');
        buttons.forEach((item) => { item.disabled = false; });
        button.textContent = originalText;
      }
    }
  }
  customElements.define('lw-cart-complements', CartComplements);
}


